import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../hooks/useAuth';
import { bilingualLines } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';
import {
  reviewApi,
  ReviewApiError,
  type AdminPickReview,
  type AdminReview,
  type ReviewSource,
} from '../../services/reviewApi';

type StatusFilter = 'pending' | 'approved' | 'rejected' | 'all';

const FILTERS: StatusFilter[] = ['pending', 'approved', 'rejected', 'all'];
const SOURCES: { key: ReviewSource; label: string }[] = [
  { key: 'activity', label: 'Activities' },
  { key: 'pick', label: 'YOYO Picks' },
];

type Row = AdminReview | AdminPickReview;
const isPickRow = (r: Row): r is AdminPickReview => 'pick' in r;

function fmt(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US');
}

const statusBadge: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

export default function ReviewModeration() {
  const siteLanguage = useSiteLanguage();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [searchParams, setSearchParams] = useSearchParams();
  const source: ReviewSource = searchParams.get('source') === 'pick' ? 'pick' : 'activity';
  const [filter, setFilter] = useState<StatusFilter>('pending');
  const [items, setItems] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  // Only the most recent load may write state, so a filter switch (or an action
  // reload racing a filter change) can't be overwritten by a stale response.
  const reqRef = useRef(0);
  const load = useCallback(async () => {
    const reqId = ++reqRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await reviewApi.adminList(filter, 1, 50, source);
      if (reqId !== reqRef.current) return;
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      if (reqId !== reqRef.current) return;
      setError(err instanceof ReviewApiError ? err.message : 'Failed to load reviews');
    } finally {
      if (reqId === reqRef.current) setLoading(false);
    }
  }, [filter, source]);

  useEffect(() => {
    if (isAuthenticated) load();
  }, [isAuthenticated, load]);

  const run = async (id: string, fn: () => Promise<unknown>) => {
    setBusyId(id);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof ReviewApiError ? err.message : 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const onApprove = (r: Row) => run(r.id, () => reviewApi.adminApprove(r.id, source));

  const onReject = (r: Row) => {
    const reason = window.prompt(
      'Rejection reason (shown to the author):',
      r.rejectionReason ?? ''
    );
    if (reason === null) return;
    if (!reason.trim()) return;
    return run(r.id, () => reviewApi.adminReject(r.id, reason.trim(), source));
  };

  const onDelete = (r: Row) => {
    if (!window.confirm('Delete this review? This cannot be undone.')) return;
    return run(r.id, () => reviewApi.adminRemove(r.id, source));
  };

  const onReply = (r: Row) => {
    const reply = window.prompt(
      'Official reply (shown publicly as YOYO official):',
      r.adminReply ?? ''
    );
    if (reply === null) return;
    if (!reply.trim()) return;
    return run(r.id, () => reviewApi.adminSetReply(r.id, reply.trim(), source));
  };

  const onDeleteReply = (r: Row) => {
    if (!window.confirm('Remove the official reply?')) return;
    return run(r.id, () => reviewApi.adminDeleteReply(r.id, source));
  };

  return (
    <AdminLayout title="Reviews">
      <div className="mb-3 flex gap-2">
        {SOURCES.map((s) => (
          <button
            key={s.key}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              if (s.key === 'pick') next.set('source', 'pick');
              else next.delete('source');
              setSearchParams(next, { replace: true });
            }}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${
              source === s.key
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div className="mb-4 flex gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize ${
              filter === f ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-gray-600">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-gray-600">No reviews.</p>
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-3">{total} total</p>
          <div className="space-y-4">
            {items.map((r) => (
              <div key={r.id} className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <a
                      href={isPickRow(r) ? `/picks/${r.pick.slug}` : `/activity/${r.activity.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold text-gray-900 hover:text-primary"
                    >
                      {bilingualLines(
                        isPickRow(r) ? r.pick.titleCN : r.activity.titleCN,
                        isPickRow(r) ? r.pick.titleEN : r.activity.titleEN,
                        siteLanguage
                      )
                        .filter(Boolean)
                        .join(' / ')}
                    </a>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {r.user.name || '(no name)'} · {r.user.email}
                    </p>
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full capitalize ${statusBadge[r.status] ?? 'bg-gray-100 text-gray-700'}`}
                  >
                    {r.status}
                  </span>
                </div>

                <div className="mt-3 text-sm text-gray-800">
                  <p className="text-xs text-gray-400 mb-1">
                    Display name: {r.displayNameMode}
                    {r.authorDisplayName ? ` (${r.authorDisplayName})` : ''}
                    {isPickRow(r) &&
                      ` · ${r.verdict === 'RECOMMEND' ? 'Recommend' : 'Not recommend'}${
                        r.visited ? ' · Visited' : ''
                      }`}
                  </p>
                  <p className="whitespace-pre-wrap">{r.content}</p>
                </div>

                {r.status === 'rejected' && r.rejectionReason && (
                  <p className="mt-2 text-xs text-red-700">Reason: {r.rejectionReason}</p>
                )}

                {r.adminReply && (
                  <div className="mt-3 ml-3 pl-3 border-l-2 border-gray-200">
                    <span className="text-xs font-semibold text-primary">YOYO official</span>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{r.adminReply}</p>
                  </div>
                )}

                <div className="mt-3 flex flex-wrap gap-2 text-sm">
                  <button
                    disabled={busyId === r.id || r.status === 'approved'}
                    onClick={() => onApprove(r)}
                    className="px-3 py-1 rounded border border-green-600 text-green-700 hover:bg-green-50 disabled:opacity-40"
                  >
                    Approve
                  </button>
                  <button
                    disabled={busyId === r.id || r.status === 'rejected'}
                    onClick={() => onReject(r)}
                    className="px-3 py-1 rounded border border-red-500 text-red-600 hover:bg-red-50 disabled:opacity-40"
                  >
                    Reject
                  </button>
                  <button
                    disabled={busyId === r.id}
                    onClick={() => onReply(r)}
                    className="px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                  >
                    {r.adminReply ? 'Edit reply' : 'Reply'}
                  </button>
                  {r.adminReply && (
                    <button
                      disabled={busyId === r.id}
                      onClick={() => onDeleteReply(r)}
                      className="px-3 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                    >
                      Remove reply
                    </button>
                  )}
                  <button
                    disabled={busyId === r.id}
                    onClick={() => onDelete(r)}
                    className="px-3 py-1 rounded border border-red-700 text-red-700 hover:bg-red-50 disabled:opacity-40"
                  >
                    Delete
                  </button>
                  <span className="ml-auto text-xs text-gray-400 self-center">
                    {fmt(r.createdAt)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </AdminLayout>
  );
}
